<script setup>
// Nexus Neural: a IA da empresa trabalhando, como uma teia de agentes que
// passam informação uns para os outros. Feita para o gestor deixar aberta numa
// TV (modo TV). Os passos vêm dos turnos que a IA registra (ver
// Neuxs::Ia::Teia), lidos a cada poucos segundos; nada do que o cliente ou a
// IA escreveu chega aqui.
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { createNexusApi } from '../nexus/api';
import messages from '../nexus/i18n';
import {
  criaMotor,
  desenhaPulso,
  CORES,
  idDoAgente,
  ZOOM_MINIMO,
  ZOOM_MAXIMO,
} from './motor';

const LEITURA_DE_EVENTOS = 4000;
const LEITURA_DE_NUMEROS = 15000;
const BALDES = 60;
const INTERVALO_DO_BALDE = 4000;
const MAXIMO_NO_FLUXO = 40;
const MARCO = 25;
// O zoom fica salvo neste navegador: cada tela (TV, notebook) guarda o seu.
const CHAVE_DO_ZOOM = 'neuxs:teia:zoom';
const PASSO_DO_ZOOM = 1.25;

const zoomSalvo = () => {
  try {
    return Number(window.localStorage.getItem(CHAVE_DO_ZOOM)) || 1;
  } catch {
    return 1;
  }
};
const salvaZoom = valor => {
  try {
    window.localStorage.setItem(CHAVE_DO_ZOOM, String(valor));
  } catch {
    // Sem armazenamento (aba anônima): o zoom vale só enquanto a tela está aberta.
  }
};

const { t } = useI18n({ messages });
const { accountId } = useAccount();
const api = computed(() => createNexusApi(accountId.value));

const raiz = ref(null);
const tela = ref(null);
const pulso = ref(null);
const estrutura = ref(null);
const numeros = ref(null);
const erro = ref(false);
const fluxo = ref([]);
const selecionado = ref(null);
const tv = ref(false);
const marco = ref('');
const hora = ref('');
const baldes = ref(Array(BALDES).fill(0));
const zoom = ref(zoomSalvo());
// Tela de toque (celular, tablet): a dica fala de dedos, não de mouse.
const toque = window.matchMedia?.('(pointer: coarse)').matches || false;

let motor = null;
let cursor = 0;
let dia = null;
let ultimoMarco = 0;
const relogios = [];

const agentes = computed(() => estrutura.value?.agentes || []);
const nomeDoAgente = chave =>
  agentes.value.find(agente => agente.chave === chave)?.nome || chave || '';
const nomeDaFerramenta = chave =>
  estrutura.value?.ferramentas.find(f => f.chave === chave)?.nome || chave;
const corDoAgente = chave => motor?.no(idDoAgente(chave))?.cor || CORES.acento;

const formata = n => Number(n || 0).toLocaleString('pt-BR');
const umaCasa = n =>
  Number(n || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
const taxa = computed(() => {
  const fechados = (numeros.value?.resolvidos || 0) + (numeros.value?.equipe || 0);
  return fechados ? Math.round((numeros.value.resolvidos / fechados) * 100) : null;
});
const horas = computed(() =>
  umaCasa(
    ((numeros.value?.resolvidos || 0) *
      (estrutura.value?.minutos_por_atendimento || 0)) /
      60
  )
);
const assuntos = computed(() => {
  const lista = Object.entries(numeros.value?.etiquetas || {});
  const maior = Math.max(1, ...lista.map(([, total]) => total));
  return lista.map(([nome, total]) => ({ nome, total, largura: (total / maior) * 100 }));
});

// ---------- números ----------
// Os números do servidor chegam com até alguns segundos de atraso; os passos
// animados somam na hora. Fica o maior, para o contador nunca andar para trás
// (no dia seguinte o servidor zera, e aí vale ele).
const juntaNumeros = novos => {
  const hoje = new Date().toDateString();
  const atuais = numeros.value;
  if (!atuais || dia !== hoje) {
    dia = hoje;
    numeros.value = novos;
    return;
  }
  const maior = (a, b) => Math.max(a || 0, b || 0);
  const agentesJuntos = { ...novos.agentes };
  Object.keys(agentesJuntos).forEach(chave => {
    const antes = atuais.agentes?.[chave] || {};
    const depois = agentesJuntos[chave];
    agentesJuntos[chave] = {
      ...depois,
      atendidos: maior(antes.atendidos, depois.atendidos),
      resolvidos: maior(antes.resolvidos, depois.resolvidos),
      equipe: maior(antes.equipe, depois.equipe),
    };
  });
  numeros.value = {
    ...novos,
    atendidos: maior(atuais.atendidos, novos.atendidos),
    resolvidos: maior(atuais.resolvidos, novos.resolvidos),
    equipe: maior(atuais.equipe, novos.equipe),
    passagens: maior(atuais.passagens, novos.passagens),
    agentes: agentesJuntos,
  };
};

const confereMarco = () => {
  const resolvidos = numeros.value?.resolvidos || 0;
  const atual = Math.floor(resolvidos / MARCO) * MARCO;
  if (!ultimoMarco) {
    ultimoMarco = atual;
    return;
  }
  if (atual > ultimoMarco) {
    ultimoMarco = atual;
    marco.value = t('NEUXS_NEXUS.TEIA.MARCO', { n: formata(atual) });
    relogios.push(setTimeout(() => (marco.value = ''), 4000));
  }
};

const soma = (campo, chave) => {
  const n = numeros.value;
  if (!n) return;
  n[campo] = (n[campo] || 0) + 1;
  if (chave && n.agentes?.[chave]) n.agentes[chave][campo] = (n.agentes[chave][campo] || 0) + 1;
};

// ---------- fluxo neural ----------
const linhaDo = passo => {
  const P = 'NEUXS_NEXUS.TEIA.PASSOS';
  const de = nomeDoAgente(passo.de).toUpperCase();
  switch (passo.tipo) {
    case 'chega':
      return { msg: `${passo.canal.toUpperCase()} → ${nomeDoAgente(passo.para).toUpperCase()}`, det: t(`${P}.chega`), cor: CORES.acento };
    case 'passa':
      return { msg: `${de} → ${nomeDoAgente(passo.para).toUpperCase()}`, det: t(`${P}.passa`), cor: corDoAgente(passo.para) };
    case 'consulta':
      return {
        msg: `${de} ⇄ ${nomeDaFerramenta(passo.ferramenta).toUpperCase()}`,
        det: t(passo.erro ? `${P}.consulta_erro` : `${P}.consulta`),
        cor: passo.erro ? CORES.equipe : corDoAgente(passo.de),
      };
    case 'responde':
      return { msg: de, det: t(`${P}.responde`), cor: corDoAgente(passo.de) };
    case 'falha':
      return { msg: de, det: t(`${P}.falha`), cor: CORES.equipe };
    case 'resolve':
      return { msg: de, det: t(passo.cortesia ? `${P}.cortesia` : `${P}.resolve`), cor: CORES.ok };
    case 'equipe':
      return {
        msg: `${de} → ${t('NEUXS_NEXUS.TEIA.EQUIPE_HUMANA')}`,
        det: passo.motivo ? t(`${P}.equipe_motivo`, { motivo: passo.motivo }) : t(`${P}.equipe`),
        cor: CORES.equipe,
      };
    default:
      return null;
  }
};

let sequencia = 0;
// Uma linha no fluxo neural; `em` é a hora do passo (no histórico, a do turno).
const registraNoFluxo = (passo, em = Date.now()) => {
  const linha = linhaDo(passo);
  if (!linha) return;
  sequencia += 1;
  fluxo.value = [
    {
      id: sequencia,
      hora: new Date(em).toLocaleTimeString('pt-BR'),
      tag: t(`NEUXS_NEXUS.TEIA.TAGS.${passo.tipo}`),
      ...linha,
    },
    ...fluxo.value,
  ].slice(0, MAXIMO_NO_FLUXO);
};

// No pulso, o balde da hora do passo (o último é o de agora).
const contaNoPulso = (em, quantos = 1) => {
  const balde = BALDES - 1 - Math.floor((Date.now() - em) / INTERVALO_DO_BALDE);
  if (balde >= 0 && balde < BALDES) baldes.value[balde] += quantos;
};

// Passo animado agora: entra no pulso, no fluxo e soma nos números.
const aoPasso = passo => {
  contaNoPulso(Date.now());
  if (passo.tipo === 'chega') soma('atendidos', passo.para);
  if (passo.tipo === 'resolve') {
    soma('resolvidos', passo.de);
    confereMarco();
  }
  if (passo.tipo === 'equipe') soma('equipe', passo.de);
  if (passo.tipo === 'passa') soma('passagens');
  registraNoFluxo(passo);
};

// O que aconteceu antes de a tela abrir (ou com a aba escondida): entra no
// pulso e no fluxo sem animar. Os números já vêm do servidor com isso.
const registraSemAnimar = eventos => {
  eventos.forEach(evento => {
    contaNoPulso(evento.em, evento.passos.length);
    evento.passos.forEach(passo => registraNoFluxo(passo, evento.em));
  });
};

// ---------- leitura ----------
const leEventos = async () => {
  if (!motor) return;
  try {
    const { data } = await api.value.eventosDaTeia(cursor);
    cursor = data.cursor;
    if (numeros.value) numeros.value.agora = data.agora;
    if (!data.eventos.length) return;
    // Aba escondida: não acumula animação para depois, só registra.
    if (document.hidden) {
      registraSemAnimar(data.eventos);
      return;
    }
    const primeiro = data.eventos[0].em;
    data.eventos.forEach(evento => {
      const atraso = Math.min(LEITURA_DE_EVENTOS - 500, Math.max(0, evento.em - primeiro));
      motor.toca(evento, atraso + Math.random() * 300, aoPasso);
    });
  } catch {
    // Tenta de novo na próxima leitura.
  }
};

const leNumeros = async () => {
  try {
    const { data } = await api.value.numerosDaTeia();
    juntaNumeros(data);
    motor?.numeros(numeros.value);
    confereMarco();
  } catch {
    // Os números ficam como estão até a próxima leitura.
  }
};

const carrega = async () => {
  try {
    const { data } = await api.value.teia();
    estrutura.value = data;
    cursor = data.cursor;
    juntaNumeros(data.numeros);
    confereMarco();
    await nextTick();
    if (!tela.value || !data.agentes.length) return;
    motor = criaMotor(tela.value, {
      zoomInicial: zoom.value,
      aoSelecionar: no => {
        selecionado.value = no;
      },
      aoMudarZoom: valor => {
        zoom.value = valor;
        salvaZoom(valor);
      },
    });
    motor.monta(data);
    motor.numeros(numeros.value);
    // Depois do motor: as cores das linhas são as dos agentes na teia.
    registraSemAnimar(data.historico || []);
    relogios.push(setInterval(leEventos, LEITURA_DE_EVENTOS));
    relogios.push(setInterval(leNumeros, LEITURA_DE_NUMEROS));
  } catch {
    erro.value = true;
  }
};

const anda = () => {
  hora.value = new Date().toLocaleTimeString('pt-BR');
  if (pulso.value) desenhaPulso(pulso.value, baldes.value);
};
const viraBalde = () => {
  baldes.value = [...baldes.value.slice(1), 0];
};

// ---------- cartão do nó ----------
const media = ms => `${umaCasa((ms || 0) / 1000)} s`;
const cartao = computed(() => {
  const no = selecionado.value;
  if (!no || !numeros.value) return null;
  const C = 'NEUXS_NEXUS.TEIA.CARTAO';
  const n = numeros.value;
  const base = { tipo: t(`NEUXS_NEXUS.TEIA.TIPOS.${no.tipo}`), nome: no.nome, cor: no.cor || CORES.nucleo };
  if (no.tipo === 'agente') {
    const dados = n.agentes?.[no.dados.chave] || {};
    const fechados = (dados.resolvidos || 0) + (dados.equipe || 0);
    const pct = fechados ? ` (${Math.round(((dados.resolvidos || 0) / fechados) * 100)}%)` : '';
    return {
      ...base,
      texto: no.dados.descricao,
      linhas: [
        [t(`${C}.ATENDIDOS`), formata(dados.atendidos)],
        [t(`${C}.RESOLVEU`), `${formata(dados.resolvidos)}${pct}`],
        [t(`${C}.PASSOU`), formata(dados.equipe)],
        [t(`${C}.RESPOSTA`), media(dados.resposta_ms)],
      ],
      chips: no.dados.ferramentas.map(nomeDaFerramenta),
    };
  }
  if (no.tipo === 'ferramenta') {
    return {
      ...base,
      texto: t(`${C}.FERRAMENTA`),
      linhas: [[t(`${C}.CONSULTAS`), formata(n.ferramentas?.[no.dados.chave])]],
      chipsTitulo: t(`${C}.USADA_POR`),
      chips: no.dados.agentes.map(nomeDoAgente),
    };
  }
  if (no.tipo === 'canal') {
    return { ...base, texto: t(`${C}.CANAL`), linhas: [[t(`${C}.CLIENTES`), formata(n.canais?.[no.nome])]] };
  }
  if (no.tipo === 'equipe') {
    return { ...base, texto: t(`${C}.EQUIPE`), linhas: [[t(`${C}.RECEBIDOS`), formata(n.equipe)]] };
  }
  return {
    ...base,
    nome: 'Nexus',
    texto: t(`${C}.NUCLEO`),
    linhas: [
      [t(`${C}.AGENTES_ATIVOS`), formata(agentes.value.length)],
      [t('NEUXS_NEXUS.TEIA.AGORA'), formata(n.agora)],
      [t('NEUXS_NEXUS.TEIA.PASSAGENS'), formata(n.passagens)],
      [t(`${C}.MODELO`), estrutura.value.modelo],
    ],
  };
});
const fechaCartao = () => {
  selecionado.value = null;
  motor?.seleciona(null);
};
const selecionaAgente = chave => {
  const id = idDoAgente(chave);
  const no = selecionado.value?.id === id ? null : motor?.no(id);
  selecionado.value = no || null;
  motor?.seleciona(no ? id : null);
};

// ---------- zoom ----------
const aproxima = () => motor?.zoom(zoom.value * PASSO_DO_ZOOM);
const afasta = () => motor?.zoom(zoom.value / PASSO_DO_ZOOM);
const zoomPadrao = () => {
  motor?.centraliza();
  motor?.zoom(1);
};

// ---------- modo TV ----------
const alternaTv = async () => {
  const ligar = !tv.value;
  tv.value = ligar;
  motor?.modoTv(ligar);
  try {
    if (ligar && raiz.value?.requestFullscreen) await raiz.value.requestFullscreen();
    if (!ligar && document.fullscreenElement) await document.exitFullscreen();
  } catch {
    // Sem tela cheia (navegador recusou): o modo TV segue na área da página.
  }
};
const aoSairDaTelaCheia = () => {
  if (!document.fullscreenElement && tv.value) {
    tv.value = false;
    motor?.modoTv(false);
  }
};
const aoTeclar = evento => {
  if (evento.key === 'Escape') fechaCartao();
};

onMounted(() => {
  carrega();
  anda();
  relogios.push(setInterval(anda, 500));
  relogios.push(setInterval(viraBalde, INTERVALO_DO_BALDE));
  document.addEventListener('fullscreenchange', aoSairDaTelaCheia);
  document.addEventListener('keydown', aoTeclar);
});
onUnmounted(() => {
  relogios.forEach(id => {
    clearInterval(id);
    clearTimeout(id);
  });
  motor?.destroi();
  document.removeEventListener('fullscreenchange', aoSairDaTelaCheia);
  document.removeEventListener('keydown', aoTeclar);
  if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
});

// As cores da legenda são as do desenho.
const LEGENDA = {
  agente: CORES.acento,
  ferramenta: CORES.ferramenta,
  equipe: CORES.equipe,
};
</script>

<template>
  <section ref="raiz" class="teia" :class="{ 'teia--tv': tv }">
    <header class="topo">
      <div class="marca">
        <svg width="34" height="34" viewBox="0 0 38 38" aria-hidden="true">
          <polygon points="19,2 34,10.5 34,27.5 19,36 4,27.5 4,10.5" fill="none" stroke="currentColor" stroke-width="1.5" />
          <circle cx="19" cy="19" r="4" fill="currentColor" />
          <g stroke="currentColor" stroke-width="1" opacity=".7">
            <line x1="19" y1="19" x2="19" y2="7" />
            <line x1="19" y1="19" x2="29" y2="25" />
            <line x1="19" y1="19" x2="9" y2="25" />
          </g>
        </svg>
        <div>
          <h1>{{ t('NEUXS_NEXUS.ABAS.TEIA') }}</h1>
          <p v-if="estrutura">
            {{ t('NEUXS_NEXUS.TEIA.SUBTITULO', { assistente: estrutura.assistente }) }}
          </p>
        </div>
      </div>
      <div class="estado">
        <span v-if="estrutura" class="ao-vivo">
          <i />{{ t('NEUXS_NEXUS.TEIA.AO_VIVO', { n: agentes.length }) }}
        </span>
        <span class="hora">{{ hora }}</span>
      </div>
      <button v-if="estrutura" type="button" class="botao" @click="alternaTv">
        {{ tv ? t('NEUXS_NEXUS.TEIA.SAIR_TV') : t('NEUXS_NEXUS.TEIA.MODO_TV') }}
      </button>
    </header>

    <div v-if="erro" class="aviso">{{ t('NEUXS_NEXUS.TEIA.ERRO') }}</div>
    <div v-else-if="!estrutura" class="aviso"><Spinner /></div>
    <div v-else-if="!agentes.length" class="aviso">
      {{ t('NEUXS_NEXUS.TEIA.SEM_AGENTES') }}
    </div>

    <template v-else>
      <aside class="painel esquerda">
        <div class="agora">
          <span class="v">{{ formata(numeros?.agora) }}</span>
          <span class="l">{{ t('NEUXS_NEXUS.TEIA.AGORA') }}</span>
        </div>
        <div class="kpis">
          <div class="kpi">
            <div class="v">{{ formata(numeros?.atendidos) }}</div>
            <div class="l">{{ t('NEUXS_NEXUS.TEIA.ATENDIDOS') }}</div>
          </div>
          <div class="kpi ok">
            <div class="v">{{ formata(numeros?.resolvidos) }}</div>
            <div class="l">
              {{ t('NEUXS_NEXUS.TEIA.RESOLVIDOS') }}
              <template v-if="taxa !== null">({{ taxa }}%)</template>
            </div>
          </div>
          <div class="kpi alerta">
            <div class="v">{{ formata(numeros?.equipe) }}</div>
            <div class="l">{{ t('NEUXS_NEXUS.TEIA.EQUIPE') }}</div>
          </div>
          <div class="kpi">
            <div class="v">{{ formata(numeros?.passagens) }}</div>
            <div class="l">{{ t('NEUXS_NEXUS.TEIA.PASSAGENS') }}</div>
          </div>
          <div class="kpi">
            <div class="v">{{ media(numeros?.resposta_ms) }}</div>
            <div class="l">{{ t('NEUXS_NEXUS.TEIA.RESPOSTA') }}</div>
          </div>
          <div class="kpi ok" :title="t('NEUXS_NEXUS.TEIA.HORAS_DICA', { min: estrutura.minutos_por_atendimento })">
            <div class="v">{{ horas }} h</div>
            <div class="l">{{ t('NEUXS_NEXUS.TEIA.HORAS') }}</div>
          </div>
        </div>
        <div>
          <div class="titulo">
            {{ t('NEUXS_NEXUS.TEIA.AGENTES') }}
            <b>{{ t('NEUXS_NEXUS.TEIA.AGENTES_DICA') }}</b>
          </div>
          <div class="agentes">
            <button
              v-for="agente in agentes"
              :key="agente.chave"
              type="button"
              class="agente"
              :class="{ sel: selecionado?.id === idDoAgente(agente.chave) }"
              :style="{ '--c': corDoAgente(agente.chave) }"
              @click="selecionaAgente(agente.chave)"
            >
              <span class="ponto" />
              <span class="n">{{ agente.nome }}</span>
              <span class="c">
                {{ formata(numeros?.agentes?.[agente.chave]?.resolvidos) }} /
                {{ formata(numeros?.agentes?.[agente.chave]?.atendidos) }}
              </span>
              <span class="barra">
                <i
                  :style="{
                    width: `${numeros?.agentes?.[agente.chave]?.atendidos ? (numeros.agentes[agente.chave].resolvidos / numeros.agentes[agente.chave].atendidos) * 100 : 0}%`,
                  }"
                />
              </span>
            </button>
          </div>
        </div>
      </aside>

      <main class="palco">
        <canvas ref="tela" :aria-label="t('NEUXS_NEXUS.ABAS.TEIA')" />
        <div class="marco" :class="{ mostra: marco }">◆ {{ marco }}</div>
        <div v-if="cartao" class="painel cartao" :style="{ '--c': cartao.cor }">
          <button type="button" class="fecha" :aria-label="t('NEUXS_NEXUS.TEIA.CARTAO.FECHAR')" @click="fechaCartao">×</button>
          <div class="tipo">{{ cartao.tipo }}</div>
          <h3>{{ cartao.nome }}</h3>
          <p v-if="cartao.texto">{{ cartao.texto }}</p>
          <dl>
            <template v-for="[rotulo, valor] in cartao.linhas" :key="rotulo">
              <dt>{{ rotulo }}</dt>
              <dd>{{ valor }}</dd>
            </template>
          </dl>
          <div v-if="cartao.chips?.length" class="chips">
            <span v-if="cartao.chipsTitulo" class="chips-titulo">{{ cartao.chipsTitulo }}</span>
            <span v-for="chip in cartao.chips" :key="chip">{{ chip }}</span>
          </div>
        </div>
        <div class="legenda">
          <span><svg width="10" height="10"><circle cx="5" cy="5" r="4" :fill="LEGENDA.agente" /></svg>{{ t('NEUXS_NEXUS.TEIA.LEGENDA.AGENTE') }}</span>
          <span><svg width="10" height="10"><path d="M5 0L10 5L5 10L0 5Z" :fill="LEGENDA.ferramenta" /></svg>{{ t('NEUXS_NEXUS.TEIA.LEGENDA.FERRAMENTA') }}</span>
          <span><svg width="11" height="10"><path d="M3 0h5l3 5-3 5H3L0 5Z" :fill="LEGENDA.equipe" /></svg>{{ t('NEUXS_NEXUS.TEIA.LEGENDA.EQUIPE') }}</span>
          <span><svg width="10" height="10"><circle cx="5" cy="5" r="3.5" fill="none" stroke="currentColor" /></svg>{{ t('NEUXS_NEXUS.TEIA.LEGENDA.CANAL') }}</span>
        </div>
        <div class="dica">{{ toque ? t('NEUXS_NEXUS.TEIA.DICA_TOQUE') : t('NEUXS_NEXUS.TEIA.DICA') }}</div>
        <div class="zoom" role="group" :aria-label="t('NEUXS_NEXUS.TEIA.ZOOM.TITULO')">
          <button
            type="button"
            :disabled="zoom <= ZOOM_MINIMO"
            :title="t('NEUXS_NEXUS.TEIA.ZOOM.AFASTA')"
            :aria-label="t('NEUXS_NEXUS.TEIA.ZOOM.AFASTA')"
            @click="afasta"
          >
            −
          </button>
          <button
            type="button"
            class="pct"
            :title="t('NEUXS_NEXUS.TEIA.ZOOM.PADRAO')"
            @click="zoomPadrao"
          >
            {{ Math.round(zoom * 100) }}%
          </button>
          <button
            type="button"
            :disabled="zoom >= ZOOM_MAXIMO"
            :title="t('NEUXS_NEXUS.TEIA.ZOOM.APROXIMA')"
            :aria-label="t('NEUXS_NEXUS.TEIA.ZOOM.APROXIMA')"
            @click="aproxima"
          >
            +
          </button>
        </div>
        <div class="tv-numeros">
          <div>
            <div class="v">{{ formata(numeros?.agora) }}</div>
            <div class="l">{{ t('NEUXS_NEXUS.TEIA.AGORA') }}</div>
          </div>
          <div>
            <div class="v">{{ formata(numeros?.resolvidos) }}</div>
            <div class="l">{{ t('NEUXS_NEXUS.TEIA.RESOLVIDOS') }}</div>
          </div>
          <div>
            <div class="v">{{ horas }} h</div>
            <div class="l">{{ t('NEUXS_NEXUS.TEIA.HORAS') }}</div>
          </div>
        </div>
      </main>

      <aside class="painel direita">
        <div class="titulo">{{ t('NEUXS_NEXUS.TEIA.FLUXO') }}</div>
        <p v-if="!fluxo.length" class="vazio">{{ t('NEUXS_NEXUS.TEIA.FLUXO_VAZIO') }}</p>
        <ul class="fluxo">
          <li v-for="linha in fluxo" :key="linha.id" :style="{ '--c': linha.cor }">
            <time>{{ linha.hora }}</time>
            <span class="tag">{{ linha.tag }}</span>
            <span class="msg">{{ linha.msg }}</span>
            <span class="det">{{ linha.det }}</span>
          </li>
        </ul>
      </aside>

      <section class="rodape">
        <div class="painel pulso">
          <div class="titulo">
            {{ t('NEUXS_NEXUS.TEIA.PULSO') }}
            <b>{{ t('NEUXS_NEXUS.TEIA.PULSO_DICA') }}</b>
          </div>
          <canvas ref="pulso" />
        </div>
        <div class="painel assuntos">
          <div class="titulo">
            {{ t('NEUXS_NEXUS.TEIA.ASSUNTOS') }}
            <b>{{ t('NEUXS_NEXUS.TEIA.ASSUNTOS_DICA') }}</b>
          </div>
          <div v-if="assuntos.length" class="lista">
            <template v-for="assunto in assuntos" :key="assunto.nome">
              <span>{{ assunto.nome }}</span>
              <span class="barra"><i :style="{ width: `${assunto.largura}%` }" /></span>
              <span class="q">{{ formata(assunto.total) }}</span>
            </template>
          </div>
          <p v-else class="vazio">{{ t('NEUXS_NEXUS.TEIA.SEM_ASSUNTOS') }}</p>
        </div>
      </section>
    </template>
  </section>
</template>

<style scoped>
/* Tela escura de propósito, nos dois temas do painel: é a vitrine da IA. */
.teia {
  --fundo: #050a14;
  --painel: rgba(10, 20, 38, 0.72);
  --linha: #17284a;
  --texto: #d8e7ff;
  --apagado: #7088ab;
  --acento: #4fb3ff;
  --acento-leve: rgba(79, 179, 255, 0.14);
  --ok: #6fe0b8;
  --alerta: #e9b47f;
  --mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  height: 100%;
  width: 100%;
  display: grid;
  gap: 12px;
  padding: 12px 16px;
  grid-template-columns: clamp(220px, 19vw, 280px) minmax(0, 1fr) clamp(260px, 23vw, 340px);
  grid-template-rows: auto minmax(0, 1fr) 150px;
  grid-template-areas: 'topo topo topo' 'esquerda palco direita' 'rodape rodape rodape';
  background: var(--fundo);
  color: var(--texto);
  overflow: hidden;
}
.topo {
  grid-area: topo;
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
}
.marca {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--acento);
}
.marca h1 {
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--texto);
}
.marca p {
  margin: 2px 0 0;
  font-size: 12.5px;
  color: var(--apagado);
}
.estado {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-left: auto;
  font: 500 12px var(--mono);
  color: var(--apagado);
}
.ao-vivo {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: var(--texto);
}
.ao-vivo i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ok);
  box-shadow: 0 0 10px var(--ok);
  animation: pisca 1.6s infinite;
}
@keyframes pisca {
  50% {
    opacity: 0.35;
  }
}
.hora {
  font-size: 17px;
  font-weight: 600;
  color: var(--texto);
  font-variant-numeric: tabular-nums;
}
.botao {
  background: var(--acento-leve);
  border: 1px solid var(--linha);
  color: var(--texto);
  padding: 7px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.botao:hover {
  border-color: var(--acento);
}
.aviso {
  grid-column: 1 / -1;
  grid-row: 2 / -1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 60px;
  color: var(--apagado);
}

.painel {
  background: var(--painel);
  border: 1px solid var(--linha);
  border-radius: 6px;
  position: relative;
  min-width: 0;
  min-height: 0;
}
.painel::before,
.painel::after {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  border: 1px solid var(--acento);
  opacity: 0.7;
}
.painel::before {
  top: -1px;
  left: -1px;
  border-right: 0;
  border-bottom: 0;
}
.painel::after {
  bottom: -1px;
  right: -1px;
  border-left: 0;
  border-top: 0;
}
.titulo {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--apagado);
  margin: 0 0 10px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
.titulo b {
  color: var(--acento);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: none;
}
.vazio {
  font-size: 13px;
  color: var(--apagado);
  margin: 0;
}

.esquerda {
  grid-area: esquerda;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow: auto;
}
.agora {
  display: flex;
  flex-direction: column;
}
.agora .v {
  font-size: 52px;
  line-height: 1;
  font-weight: 700;
  color: var(--acento);
  text-shadow: 0 0 24px var(--acento-leve);
  font-variant-numeric: tabular-nums;
}
.agora .l {
  font-size: 13px;
  color: var(--apagado);
}
.kpis {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 10px;
}
.kpi .v {
  font-size: 20px;
  line-height: 1.1;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.kpi .l {
  font-size: 12px;
  line-height: 1.25;
  color: var(--apagado);
}
.kpi.ok .v {
  color: var(--ok);
}
.kpi.alerta .v {
  color: var(--alerta);
}
.agentes {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.agente {
  display: grid;
  grid-template-columns: 10px 1fr auto;
  gap: 4px 8px;
  align-items: center;
  background: none;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 6px;
  text-align: left;
  color: var(--texto);
}
.agente:hover,
.agente.sel {
  border-color: var(--linha);
  background: var(--acento-leve);
}
.agente .ponto {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--c);
  box-shadow: 0 0 8px var(--c);
}
.agente .n {
  font-weight: 600;
  font-size: 13.5px;
}
.agente .c {
  font: 12px var(--mono);
  color: var(--apagado);
  font-variant-numeric: tabular-nums;
}
.agente .barra,
.lista .barra {
  grid-column: 2 / 4;
  height: 3px;
  background: var(--linha);
  border-radius: 2px;
  overflow: hidden;
}
.barra i {
  display: block;
  height: 100%;
  background: var(--c, var(--acento));
  transition: width 0.6s;
}

.direita {
  grid-area: direita;
  padding: 14px 14px 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.fluxo {
  list-style: none;
  margin: 0;
  padding: 0 0 12px;
  overflow: hidden;
  flex: 1;
  font: 12px/1.35 var(--mono);
  mask-image: linear-gradient(#000 70%, transparent);
}
.fluxo li {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1px 8px;
  padding: 6px 0;
  border-bottom: 1px solid var(--linha);
  animation: desce 0.35s ease-out;
}
.fluxo time {
  color: var(--apagado);
  font-variant-numeric: tabular-nums;
}
.fluxo .tag {
  justify-self: start;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.14em;
  color: var(--c);
  border: 1px solid var(--c);
  padding: 0 5px;
  border-radius: 2px;
}
.fluxo .msg {
  grid-column: 1 / 3;
  color: var(--c);
  clip-path: inset(0 100% 0 0);
  animation: digita 0.7s steps(28) forwards;
  overflow-wrap: anywhere;
}
.fluxo .det {
  grid-column: 1 / 3;
  color: var(--apagado);
}
@keyframes digita {
  to {
    clip-path: inset(0 0 0 0);
  }
}
@keyframes desce {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
}

.palco {
  grid-area: palco;
  position: relative;
  overflow: hidden;
  border-radius: 6px;
  background: radial-gradient(ellipse at 50% 52%, var(--acento-leve), transparent 62%);
}
/* O motor grava o tamanho em pixels da tela (largura × densidade): sem o
   tamanho no CSS, num celular ou em tela com escala o canvas fica 2 a 3
   vezes maior que o palco. */
.palco canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  touch-action: pan-x pan-y;
}
.legenda {
  position: absolute;
  left: 10px;
  bottom: 8px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  font: 11px var(--mono);
  color: var(--apagado);
  pointer-events: none;
}
.legenda span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.dica {
  position: absolute;
  right: 10px;
  top: 8px;
  font: 11px var(--mono);
  color: var(--apagado);
  pointer-events: none;
}
.zoom {
  position: absolute;
  right: 10px;
  bottom: 8px;
  display: flex;
  z-index: 1;
  border: 1px solid var(--linha);
  border-radius: 4px;
  background: var(--painel);
  overflow: hidden;
}
.zoom button {
  min-width: 30px;
  height: 28px;
  padding: 0 8px;
  background: none;
  border: 0;
  color: var(--texto);
  font: 500 13px var(--mono);
}
.zoom button + button {
  border-left: 1px solid var(--linha);
}
.zoom button:hover:not(:disabled) {
  background: var(--acento-leve);
}
.zoom button:disabled {
  opacity: 0.35;
}
.zoom .pct {
  min-width: 52px;
  color: var(--apagado);
  font-variant-numeric: tabular-nums;
}
.marco {
  position: absolute;
  left: 50%;
  top: 14px;
  transform: translate(-50%, -12px);
  opacity: 0;
  transition: 0.5s;
  pointer-events: none;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 8px 16px;
  border: 1px solid var(--ok);
  color: var(--ok);
  background: rgba(5, 20, 14, 0.8);
  border-radius: 3px;
  white-space: nowrap;
}
.marco.mostra {
  opacity: 1;
  transform: translate(-50%, 0);
}
.cartao {
  position: absolute;
  top: 12px;
  left: 12px;
  width: min(290px, calc(100% - 24px));
  padding: 14px;
  z-index: 2;
}
.cartao h3 {
  margin: 0 0 2px;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--c);
}
.cartao .tipo {
  font: 11px var(--mono);
  color: var(--apagado);
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.cartao p {
  margin: 8px 0 10px;
  font-size: 13.5px;
  opacity: 0.85;
}
.cartao dl {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 5px 10px;
  margin: 0;
  font-size: 13px;
}
.cartao dt {
  color: var(--apagado);
}
.cartao dd {
  margin: 0;
  font: 500 13px var(--mono);
  text-align: right;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 10px;
}
.chips span {
  font: 11px var(--mono);
  border: 1px solid var(--linha);
  padding: 2px 6px;
  border-radius: 2px;
  color: var(--apagado);
}
.chips .chips-titulo {
  border: 0;
  padding-left: 0;
}
.fecha {
  position: absolute;
  top: 6px;
  right: 6px;
  background: none;
  border: 0;
  color: var(--apagado);
  font-size: 18px;
  line-height: 1;
  padding: 4px 6px;
}
.tv-numeros {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 24px;
  display: none;
  justify-content: center;
  gap: clamp(24px, 6vw, 90px);
  pointer-events: none;
}
.tv-numeros > div {
  text-align: center;
}
.tv-numeros .v {
  font-size: clamp(30px, 4.5vw, 64px);
  line-height: 1;
  font-weight: 700;
  color: var(--acento);
  text-shadow: 0 0 30px var(--acento-leve);
  font-variant-numeric: tabular-nums;
}
.tv-numeros .l {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--apagado);
}

.rodape {
  grid-area: rodape;
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
  gap: 12px;
  min-height: 0;
}
.pulso,
.assuntos {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.pulso canvas {
  flex: 1;
  width: 100%;
  min-height: 0;
  display: block;
}
.lista {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 4px 10px;
  align-items: center;
  font-size: 12.5px;
  overflow: hidden;
}
.lista .barra {
  grid-column: auto;
  height: 4px;
}
.lista .q {
  font: 12px var(--mono);
  color: var(--apagado);
  text-align: right;
}

.teia--tv {
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  grid-template-areas: 'topo' 'palco';
}
.teia--tv .esquerda,
.teia--tv .direita,
.teia--tv .rodape,
.teia--tv .legenda,
.teia--tv .zoom,
.teia--tv .dica {
  display: none;
}
.teia--tv .tv-numeros {
  display: flex;
}

@media (max-width: 1100px) {
  .teia {
    overflow: auto;
    height: auto;
    min-height: 100%;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto;
    grid-template-areas: 'topo' 'palco' 'esquerda' 'direita' 'rodape';
  }
  .palco {
    height: max(380px, 64vh);
  }
  .direita {
    height: 340px;
  }
  .rodape {
    grid-template-columns: minmax(0, 1fr);
  }
  .pulso {
    height: 150px;
  }
}
/* Celular: tudo numa coluna, a teia com a altura da tela, a legenda e a dica
   embaixo dela e o cartão do nó subindo do rodapé do palco. */
@media (max-width: 640px) {
  .teia {
    padding: 10px;
    gap: 10px;
  }
  .topo {
    gap: 10px;
  }
  .marca h1 {
    font-size: 16px;
    letter-spacing: 0.12em;
  }
  .estado {
    margin-left: 0;
    width: 100%;
    justify-content: space-between;
  }
  .palco {
    height: 70vh;
    min-height: 360px;
  }
  .dica {
    top: 8px;
    right: 10px;
    left: 10px;
  }
  .legenda {
    bottom: 8px;
    max-width: calc(100% - 130px);
    gap: 8px;
  }
  .cartao {
    top: auto;
    left: 8px;
    right: 8px;
    bottom: 8px;
    width: auto;
    max-height: 60%;
    overflow-y: auto;
  }
  .kpis {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (prefers-reduced-motion: reduce) {
  .fluxo .msg {
    animation: none;
    clip-path: none;
  }
  .fluxo li,
  .ao-vivo i {
    animation: none;
  }
}
</style>
